import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Ensure GSAP plugins are registered
gsap.registerPlugin(ScrollTrigger);

const steps = [
  {
    number: 1,
    title: "Create Your Profile",
    description:
      "Students build a comprehensive digital portfolio showcasing their skills, projects, coursework, and career aspirations.",
  },
  {
    number: 2,
    title: "Explore Opportunities",
    description:
      "Browse through personalized internship recommendations filtered by industry, location, duration, and other preferences.",
  },
  {
    number: 3,
    title: "Apply & Interview",
    description:
      "Submit applications to preferred positions and schedule interviews through our integrated communication system.",
  },
  {
    number: 4,
    title: "Track & Succeed",
    description:
      "Monitor your application status, receive feedback, and document your internship experience for academic credit.",
  },
];

export default function HowItWorks() {
  const sectionRef = useRef(null);
  const contentLeftRef = useRef(null);
  const contentRightRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const stepItems = document.querySelectorAll(".step-item");
    const notifications = document.querySelectorAll(".notification-card");
    
    if (section && stepItems.length > 0) {
      // Title animation
      gsap.fromTo(
        titleRef.current,
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          scrollTrigger: {
            trigger: titleRef.current,
            start: "top bottom-=100",
            end: "bottom center",
            scrub: 1,
            toggleActions: "play none none reverse",
          },
        }
      );

      // Step items animation
      stepItems.forEach((step) => {
        gsap.fromTo(
          step,
          { x: -50, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            duration: 1,
            scrollTrigger: {
              trigger: step,
              start: "top bottom-=100",
              end: "bottom center",
              scrub: 1,
              toggleActions: "play none none reverse",
            },
          }
        );
      });

      // Right content animation (visualization)
      gsap.fromTo(
        contentRightRef.current,
        { x: 50, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 1,
          scrollTrigger: {
            trigger: contentRightRef.current,
            start: "top bottom-=100",
            end: "bottom center",
            scrub: 1,
            toggleActions: "play none none reverse",
          },
        }
      );

      // Notification cards animation
      notifications.forEach((notification) => {
        gsap.fromTo(
          notification,
          { y: 20, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 1,
            scrollTrigger: {
              trigger: notification,
              start: "top bottom-=100",
              end: "bottom center",
              scrub: 1,
              toggleActions: "play none none reverse",
            },
          }
        );
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [contentRightRef, sectionRef, titleRef]);

  return (
    <section
      className="py-24 bg-gray-800 relative"
      id="how-it-works"
      ref={sectionRef}
      data-section="how-it-works"
    >
      {/* Background satellite orbit visualization */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10">
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 1920 1080"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid slice"
        >
          <circle
            cx="960"
            cy="540"
            r="400"
            stroke="#4A00E0"
            strokeWidth="1"
            strokeDasharray="20 10"
          />
          <circle
            cx="960"
            cy="540"
            r="300"
            stroke="#00C2FF"
            strokeWidth="1"
            strokeDasharray="10 5"
          />
          <circle
            cx="960"
            cy="540"
            r="200"
            stroke="#FFFFFF"
            strokeWidth="1"
            strokeDasharray="5 5"
            opacity="0.3"
          />
          <path
            d="M960 140 Q 1260 540 960 940 Q 660 540 960 140"
            stroke="#4A00E0"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M760 340 Q 960 540 1160 340 Q 960 140 760 340"
            stroke="#00C2FF"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M760 740 Q 960 540 1160 740 Q 960 940 760 740"
            stroke="#00C2FF"
            strokeWidth="2"
            fill="none"
          />
        </svg>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div
          className="text-center max-w-2xl mx-auto mb-16"
          ref={titleRef}
          data-animation="fade-in"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">How It Works</h2>
          <p className="text-gray-300">
            Our streamlined process connects students with their dream
            internships in just a few simple steps.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div
            className="order-2 md:order-1"
            ref={contentLeftRef}
            data-animation="fade-in-right"
          >
            <div className="space-y-12">
              {steps.map((step, index) => (
                <div key={index} className="flex gap-6 step-item">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">
                      {step.number}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium mb-2">{step.title}</h3>
                    <p className="text-gray-300">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            className="order-1 md:order-2"
            ref={contentRightRef}
            data-animation="fade-in-left"
          >
            {/* Modern university technology visualization */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl opacity-30 blur-lg"></div>

              {/* SVG illustration of a student using the portal */}
              <div className="rounded-xl relative z-10 w-full h-auto shadow-xl bg-gray-900 p-6 border border-white border-opacity-10">
                <svg
                  className="w-full"
                  viewBox="0 0 500 400"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Student profile */}
                  <rect
                    x="50"
                    y="50"
                    width="400"
                    height="300"
                    rx="10"
                    fill="#0A1128"
                  />

                  {/* Header */}
                  <rect
                    x="70"
                    y="70"
                    width="360"
                    height="50"
                    rx="8"
                    fill="#172341"
                  />
                  <circle cx="95" cy="95" r="15" fill="#4A00E0" />
                  <rect
                    x="120"
                    y="90"
                    width="100"
                    height="10"
                    rx="2"
                    fill="white"
                  />

                  {/* Sidebar */}
                  <rect
                    x="70"
                    y="130"
                    width="100"
                    height="200"
                    rx="8"
                    fill="#172341"
                  />
                  <rect
                    x="85"
                    y="150"
                    width="70"
                    height="8"
                    rx="2"
                    fill="white"
                  />
                  <rect
                    x="85"
                    y="170"
                    width="70"
                    height="8"
                    rx="2"
                    fill="#FFFFFF80"
                  />
                  <rect
                    x="85"
                    y="190"
                    width="70"
                    height="8"
                    rx="2"
                    fill="#FFFFFF80"
                  />
                  <rect
                    x="85"
                    y="210"
                    width="70"
                    height="8"
                    rx="2"
                    fill="#FFFFFF80"
                  />
                  <rect
                    x="85"
                    y="230"
                    width="70"
                    height="8"
                    rx="2"
                    fill="#FFFFFF80"
                  />
                  <rect
                    x="85"
                    y="290"
                    width="70"
                    height="25"
                    rx="4"
                    fill="#00C2FF"
                  />

                  {/* Main content */}
                  <rect
                    x="180"
                    y="130"
                    width="250"
                    height="200"
                    rx="8"
                    fill="#172341"
                  />
                  <rect
                    x="200"
                    y="150"
                    width="150"
                    height="10"
                    rx="2"
                    fill="white"
                  />
                  <rect
                    x="200"
                    y="170"
                    width="210"
                    height="40"
                    rx="4"
                    fill="#0A1128"
                  />
                  <rect
                    x="200"
                    y="220"
                    width="210"
                    height="40"
                    rx="4"
                    fill="#0A1128"
                  />
                  <rect
                    x="200"
                    y="270"
                    width="210"
                    height="40"
                    rx="4"
                    fill="#0A1128"
                  />

                  {/* Interactive elements */}
                  <circle cx="370" cy="190" r="10" fill="#4A00E0" />
                  <circle cx="410" cy="240" r="10" fill="#00C2FF" />
                  <circle cx="380" cy="290" r="10" fill="#4A00E0" />
                </svg>
              </div>

              {/* Floating notification 1 */}
              <div className="absolute top-5 -right-6 bg-gray-800 bg-opacity-70 backdrop-blur-lg rounded-lg p-3 shadow-lg max-w-xs animate-float notification-card">
                <div className="flex items-center gap-3">
                  <i className="fas fa-bell text-blue-400"></i>
                  <p className="text-sm text-white">
                    Interview scheduled with Tech Solutions Inc.
                  </p>
                </div>
              </div>

              {/* Floating notification 2 */}
              <div className="absolute -bottom-6 -left-6 bg-gray-800 bg-opacity-70 backdrop-blur-lg rounded-lg p-3 shadow-lg max-w-xs animate-float delay-500 notification-card">
                <div className="flex items-center gap-3">
                  <i className="fas fa-check-circle text-blue-400"></i>
                  <p className="text-sm text-white">
                    Application approved! Next step: Onboarding
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
